"use client"

import { User, Package, Gift, Wallet, CheckCircle2 } from "lucide-react"
import { useSearchParams } from "next/navigation"
import { CheckoutAuthGate } from "@modules/checkout/components/checkout-auth-gate"
import { useReferralProgram } from "@lib/context/referral-program-context"

/**
 * Signed-out view of /account: the account page's own shell (teal wash, white
 * card) with the shared auth gate inside, so signing in reads as "unlock your
 * account" rather than being bounced to a separate login page.
 * (Owner call 2026-08-12 — same gate as checkout, no blur here.)
 */
export default function AccountAuthPanel({ countryCode }: { countryCode: string }) {
  // Flow params carried over from retired /login links (middleware forwards
  // redirect / verify / view here): land on the right page after sign-in, show
  // the email-verified note, and open the Create Account tab for register links.
  const searchParams = useSearchParams()
  const redirectParam = searchParams.get("redirect")
  const redirectTo =
    redirectParam && redirectParam.startsWith("/") && !redirectParam.startsWith("//")
      ? redirectParam
      : `/${countryCode}/account`
  const showVerified = searchParams.get("verify") === "true"
  const initialMode = searchParams.get("view") === "register" ? ("register" as const) : undefined

  const program = useReferralProgram()
  const perks = [
    { icon: Package, label: "Order history", note: "Track shipments and reorder in a click" },
    { icon: Wallet, label: "Store credit", note: "See your balance and apply it at checkout" },
    { icon: Gift, label: "Referrals", note: `Share your code, earn ${program.get} per referral` },
  ]

  return (
    <div className="relative min-h-screen bg-slate-50" data-testid="account-login-page">
      <div
        className="absolute top-0 left-0 w-full h-[500px] pointer-events-none"
        style={{ background: "radial-gradient(ellipse 80% 100% at 50% 0%, rgba(0,128,128,0.07), transparent 70%)" }}
      />

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-8 pb-20 md:pt-12">
        <div className="flex items-center gap-3 mb-6">
          <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#005555]/10">
            <User className="h-5 w-5 text-[#005555]" />
          </span>
          <div>
            <h1 className="font-manrope text-2xl md:text-3xl font-extrabold text-[#003366]">My Account</h1>
            <p className="text-sm text-slate-500">Sign in to view your orders, credit and referrals</p>
          </div>
        </div>

        <div className="grid gap-6 md:grid-cols-[minmax(0,1fr)_400px] items-start">
          {/* What's behind the door — makes the gate feel like the account page */}
          <div className="hidden md:block bg-white border border-slate-200 rounded-2xl shadow-sm p-8">
            <h2 className="font-manrope text-lg font-extrabold text-[#003366] mb-5">Your account includes</h2>
            <ul className="flex flex-col gap-5">
              {perks.map(({ icon: Icon, label, note }) => (
                <li key={label} className="flex items-start gap-3">
                  <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-[#008080]/10">
                    <Icon className="h-4 w-4 text-[#008080]" />
                  </span>
                  <span>
                    <span className="block text-sm font-semibold text-slate-800">{label}</span>
                    <span className="block text-sm text-slate-500">{note}</span>
                  </span>
                </li>
              ))}
            </ul>
            <p className="mt-7 text-[11px] font-semibold tracking-wide text-slate-400">
              99%+ PURITY &nbsp;·&nbsp; THIRD-PARTY TESTED &nbsp;·&nbsp; SAME-DAY SHIPPING
            </p>
          </div>

          <div className="bg-white border border-slate-200 rounded-2xl shadow-sm overflow-hidden">
            <div className="h-1.5 bg-gradient-to-r from-[#008080] via-[#00b3b3] to-[#005555]" />
            {showVerified && (
              <div className="flex items-center gap-2 bg-emerald-50 border-b border-emerald-100 px-5 py-3">
                <CheckCircle2 className="h-4 w-4 shrink-0 text-emerald-600" />
                <p className="text-sm font-medium text-emerald-800">
                  Email verified — sign in below to continue.
                </p>
              </div>
            )}
            <CheckoutAuthGate
              countryCode={countryCode}
              copy={{
                initialMode,
                redirectTo,
                bannerTitle: "Welcome back",
                bannerSubtitle: "Sign in to view your orders, credit and referrals.",
                registerBannerTitle: "Join FastPeps",
                registerBannerSubtitle: "Create your free account to order, track shipments and earn referral credit.",
                signInLabel: "Sign In",
                registerLabel: "Create Account",
              }}
            />
          </div>
        </div>
      </div>
    </div>
  )
}
